/**
 * The two clinical safety checks the app makes on the doctor's behalf.
 *
 * Both follow the same rule as everything else in this product: they warn,
 * they never block. A reading of 210/130 is exactly the one that has to be
 * recorded, and a doctor who knows the patient tolerates a drug must be able
 * to prescribe it. Refusing to save would only teach people to route around
 * the system.
 *
 * Shared by the desktop and mobile faces so the two can never disagree about
 * what counts as dangerous.
 */

import type { Vitals } from './api'

export type VitalLevel = 'normal' | 'high' | 'critical'

export interface VitalFlag {
  level: VitalLevel
  /** Said in words, because colour alone fails anyone who cannot see it. */
  label: string
}

const NORMAL: VitalFlag = { level: 'normal', label: '' }

function num(v: string | number | null | undefined): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** ACC/AHA stage 2 at 140/90, hypertensive crisis at 180/120. */
export function bpFlag(sys: number | null, dia: number | null): VitalFlag {
  if (sys == null && dia == null) return NORMAL
  const s = sys ?? 0
  const d = dia ?? 0
  if (s >= 180 || d >= 120) return { level: 'critical', label: 'crisis' }
  if (s >= 140 || d >= 90) return { level: 'high', label: 'high' }
  if (s > 0 && s < 90) return { level: 'critical', label: 'low' }
  return NORMAL
}

/** Below 94% warrants a look; below 91% is the one that gets someone moved. */
export function spo2Flag(pct: number | null): VitalFlag {
  const v = num(pct)
  if (v == null) return NORMAL
  if (v < 91) return { level: 'critical', label: 'hypoxia' }
  if (v < 94) return { level: 'high', label: 'low' }
  return NORMAL
}

export function pulseFlag(bpm: number | null): VitalFlag {
  const v = num(bpm)
  if (v == null) return NORMAL
  if (v >= 130 || v < 40) return { level: 'critical', label: v >= 130 ? 'very fast' : 'very slow' }
  if (v > 100) return { level: 'high', label: 'fast' }
  if (v < 50) return { level: 'high', label: 'slow' }
  return NORMAL
}

/** Fahrenheit, because that is what the vitals form collects. */
export function tempFlag(f: string | number | null): VitalFlag {
  const v = num(f)
  if (v == null) return NORMAL
  if (v >= 104 || v < 95) return { level: 'critical', label: v >= 104 ? 'very high' : 'low' }
  if (v >= 100.4) return { level: 'high', label: 'fever' }
  return NORMAL
}

export function respFlag(perMin: number | null): VitalFlag {
  const v = num(perMin)
  if (v == null) return NORMAL
  if (v >= 30 || v < 8) return { level: 'critical', label: v >= 30 ? 'very fast' : 'very slow' }
  if (v > 20) return { level: 'high', label: 'fast' }
  return NORMAL
}

/**
 * Thresholds depend on when the sample was taken, so an unlabelled reading is
 * judged on the loosest (random) scale rather than flagged spuriously.
 */
export function sugarFlag(value: string | number | null, type: string | null): VitalFlag {
  const v = num(value)
  if (v == null) return NORMAL
  if (v < 70) return { level: 'critical', label: 'hypo' }
  if (type === 'fasting') {
    if (v >= 200) return { level: 'critical', label: 'very high' }
    if (v >= 126) return { level: 'high', label: 'high' }
    return NORMAL
  }
  if (v >= 300) return { level: 'critical', label: 'very high' }
  if (v >= 200) return { level: 'high', label: 'high' }
  return NORMAL
}

/** Every flag on one reading, keyed the same as the Vitals row. */
export function vitalFlags(v: Pick<Vitals,
  'bpSystolic' | 'bpDiastolic' | 'pulseBpm' | 'temperatureF' | 'spo2Pct'
  | 'respiratoryRate' | 'bloodSugar' | 'bloodSugarType'>) {
  return {
    bp: bpFlag(v.bpSystolic, v.bpDiastolic),
    pulse: pulseFlag(v.pulseBpm),
    temp: tempFlag(v.temperatureF),
    spo2: spo2Flag(v.spo2Pct),
    resp: respFlag(v.respiratoryRate),
    sugar: sugarFlag(v.bloodSugar, v.bloodSugarType),
  }
}

/** The className for a flagged value, or '' when the reading is unremarkable. */
export function vitalClass(flag: VitalFlag): string {
  if (flag.level === 'critical') return 'vital-critical'
  if (flag.level === 'high') return 'vital-high'
  return ''
}

/** "210/130 (crisis)" for a screen reader and a printed sheet alike. */
export function vitalTitle(flag: VitalFlag): string | undefined {
  return flag.level === 'normal' ? undefined : flag.label
}

/* ------------------------------------------------------------------ */
/* Allergy cross-check                                                 */
/* ------------------------------------------------------------------ */

/**
 * Drug families worth catching by class rather than by exact name. A patient
 * recorded as allergic to "Penicillin" is allergic to Amoxicillin, and the
 * allergy field is free text written by whoever was at the desk, so matching
 * the literal string would miss the case that matters most.
 *
 * Deliberately short. This is a safety net for the obvious collision, not a
 * drug interaction database, and a list that cries wolf gets clicked through.
 */
const DRUG_FAMILIES: Array<{ family: string; allergyTerms: string[]; drugTerms: string[] }> = [
  {
    family: 'penicillin',
    allergyTerms: ['penicillin', 'pencillin', 'penicilin', 'amoxicillin', 'augmentin', 'ampicillin'],
    drugTerms: ['penicillin', 'amoxicillin', 'amoxycillin', 'ampicillin', 'cloxacillin',
      'piperacillin', 'augmentin', 'clavulanic', 'mox', 'amoxil'],
  },
  {
    family: 'sulpha',
    allergyTerms: ['sulpha', 'sulfa', 'sulphonamide', 'sulfonamide', 'cotrimoxazole', 'bactrim'],
    drugTerms: ['sulfamethoxazole', 'sulphamethoxazole', 'cotrimoxazole', 'trimethoprim',
      'sulfadiazine', 'bactrim', 'septran', 'septrin'],
  },
  {
    family: 'NSAID',
    allergyTerms: ['aspirin', 'nsaid', 'ibuprofen', 'diclofenac', 'brufen'],
    drugTerms: ['aspirin', 'ibuprofen', 'diclofenac', 'naproxen', 'ketorolac', 'aceclofenac',
      'indomethacin', 'brufen', 'combiflam', 'voveran'],
  },
  {
    family: 'cephalosporin',
    allergyTerms: ['cephalosporin', 'cephalexin', 'ceftriaxone', 'cefixime'],
    drugTerms: ['cephalexin', 'cefixime', 'ceftriaxone', 'cefuroxime', 'cefpodoxime', 'cefdinir'],
  },
  {
    family: 'sulphonylurea',
    allergyTerms: ['sulphonylurea', 'sulfonylurea', 'glimepiride', 'glibenclamide'],
    drugTerms: ['glimepiride', 'glibenclamide', 'gliclazide', 'glipizide'],
  },
  {
    family: 'macrolide',
    allergyTerms: ['macrolide', 'erythromycin', 'azithromycin', 'clarithromycin'],
    drugTerms: ['erythromycin', 'azithromycin', 'clarithromycin', 'roxithromycin', 'azithral'],
  },
  {
    family: 'iodine contrast',
    allergyTerms: ['iodine', 'contrast', 'iodinated'],
    drugTerms: ['iohexol', 'iopamidol', 'ioversol', 'povidone'],
  },
]

/** Lowercased words of three letters or more; punctuation is a separator. */
function terms(text: string): string[] {
  return text.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length >= 3)
}

export interface AllergyConflict {
  /** What the patient's record says, quoted back so the doctor can judge it. */
  allergy: string
  /** Why this fired: a shared drug family, or the drug's own name. */
  reason: string
}

/**
 * Checks one medicine line against the patient's recorded allergies.
 *
 * Matches on three things, cheapest first: the allergy naming the drug or its
 * generic outright, the drug name appearing inside the allergy text, and both
 * sides landing in the same family above. Returns every distinct hit, because
 * "Penicillin, Sulpha" is one field and both halves can fire.
 */
export function allergyConflicts(
  allergies: string | null | undefined,
  drugName: string | null | undefined,
  genericName?: string | null,
): AllergyConflict[] {
  if (!allergies?.trim()) return []
  const drugText = `${drugName ?? ''} ${genericName ?? ''}`.trim()
  if (!drugText) return []

  const drugWords = terms(drugText)
  if (drugWords.length === 0) return []

  const hits: AllergyConflict[] = []
  const seen = new Set<string>()
  const add = (allergy: string, reason: string) => {
    const k = `${allergy}::${reason}`
    if (seen.has(k)) return
    seen.add(k)
    hits.push({ allergy, reason })
  }

  // One allergy field often lists several things: "Penicillin, Iodine contrast".
  const entries = allergies.split(/[,;/]|\band\b/i).map((s) => s.trim()).filter(Boolean)

  for (const entry of entries) {
    const entryWords = terms(entry)
    if (entryWords.length === 0) continue

    // Direct: the same word appears on both sides.
    const shared = drugWords.find((w) => entryWords.includes(w))
    if (shared) {
      add(entry, `names ${shared}`)
      continue
    }

    // Substring either way, so "Amoxicillin 500" still matches "amoxicillin".
    const entryLower = entry.toLowerCase()
    const drugLower = drugText.toLowerCase()
    const partial = drugWords.find((w) => w.length >= 5 && entryLower.includes(w))
      ?? entryWords.find((w) => w.length >= 5 && drugLower.includes(w))
    if (partial) {
      add(entry, `names ${partial}`)
      continue
    }

    // Class: both sides sit in the same family. Short family terms have to
    // match a whole word - "mox" as a substring would drag in Moxifloxacin,
    // which is a quinolone and nothing to do with penicillin.
    const inText = (term: string, words: string[], text: string) => (
      term.length >= 6 ? words.includes(term) || text.includes(term) : words.includes(term)
    )
    for (const fam of DRUG_FAMILIES) {
      const allergyHit = fam.allergyTerms.some((t) => inText(t, entryWords, entryLower))
      if (!allergyHit) continue
      const drugHit = fam.drugTerms.some((t) => inText(t, drugWords, drugLower))
      if (drugHit) add(entry, `same ${fam.family} family`)
    }
  }

  return hits
}

/** One line fit for a banner: "Penicillin (same penicillin family)". */
export function describeConflicts(hits: AllergyConflict[]): string {
  return hits.map((h) => `${h.allergy} (${h.reason})`).join(', ')
}

/**
 * A medicine's name with its strength, without saying the strength twice.
 * Brand names often carry it already ("Glycomet 500", "Telma 40"), and the
 * drug list stores it again on its own, so a plain join printed
 * "Glycomet 500 500 mg". The strength is left off when its number already
 * stands on its own in the name.
 */
export function medicineLabel(name: string, strength: string | null | undefined): string {
  const s = strength?.trim()
  if (!s) return name
  const squash = (t: string) => t.toLowerCase().replace(/\s+/g, '')
  if (squash(name).includes(squash(s))) return name
  const figure = s.match(/^\d+(?:\.\d+)?/)?.[0]
  // Doubled backslashes: in a template string a single \d is just "d".
  if (figure && new RegExp(`(^|[^\\d.])${figure.replace('.', '\\.')}(?![\\d.])`).test(name)) return name
  return `${name} ${s}`
}
